import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Loader2, Plus, Trash2, Lock, RotateCcw, Shuffle, Info
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const isTrue = (v) => v === true || v === 1 || v === 't' || v === 'true' || v === 'TRUE';


function RotationToggle({ on, disabled, onChange }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        on ? 'bg-emerald-500' : 'bg-slate-300'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
      title={on ? 'In rotation' : 'Not in rotation'}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        on ? 'translate-x-4' : 'translate-x-0.5'
      }`} />
    </button>
  );
}


function BylineRow({ byline, onToggleRotation, onDelete, onReactivate }) {
  const inactive  = !isTrue(byline.is_active);
  const protectedRow = isTrue(byline.is_protected);

  return (
    <div className={`flex items-center justify-between gap-4 bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm ${
      inactive ? 'opacity-60' : ''
    }`}>
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-slate-900 font-medium truncate">{byline.name}</span>
        {protectedRow && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded border border-slate-200">
            <Lock size={9} /> System
          </span>
        )}
        {inactive && (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-400 rounded border border-slate-200">
            Inactive
          </span>
        )}
      </div>

      <div className="flex items-center gap-4 shrink-0">
        {!inactive && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Rotation</span>
            <RotationToggle
              on={isTrue(byline.in_rotation)}
              onChange={(next) => onToggleRotation(byline, next)}
            />
          </div>
        )}

        {inactive ? (
          <button
            onClick={() => onReactivate(byline)}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 px-2 py-1 rounded transition-colors"
          >
            <RotateCcw size={12} /> Reactivate
          </button>
        ) : protectedRow ? (
          <span className="w-[26px]" />
        ) : (
          <button
            onClick={() => onDelete(byline)}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
            title="Remove byline"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}


export default function BylineQueue() {
  const { user } = useAuth();

  const [bylines, setBylines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding]   = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/bylines');
      setBylines(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load bylines');
    } finally {
      setLoading(false);
    }
  };

  const addByline = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await api.post('/bylines', { name });
      setNewName('');
      toast.success(`Added "${name}"`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add byline');
    } finally {
      setAdding(false);
    }
  };

  const toggleRotation = async (byline, next) => {
    try {
      await api.patch(`/bylines/${byline.id}`, { in_rotation: next });
      setBylines(prev => prev.map(b => b.id === byline.id ? { ...b, in_rotation: next } : b));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update rotation');
    }
  };

  const removeByline = async (byline) => {
    if (!window.confirm(`Remove "${byline.name}" from the byline queue?\n\nExisting stories keep their byline; this just retires it from selection.`)) return;
    try {
      await api.delete(`/bylines/${byline.id}`);
      toast.success(`Removed "${byline.name}"`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove byline');
    }
  };

  const reactivateByline = async (byline) => {
    try {
      await api.patch(`/bylines/${byline.id}`, { is_active: true });
      toast.success(`Reactivated "${byline.name}"`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate byline');
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'editor') {
    return <div className="text-center py-16"><p className="text-slate-400">You do not have access to bylines.</p></div>;
  }

  const active   = bylines.filter(b => isTrue(b.is_active));
  const inactive = bylines.filter(b => !isTrue(b.is_active));
  const rotationCount = active.filter(b => isTrue(b.in_rotation)).length;

  return (
    <div className="max-w-2xl">
      <div className="mb-5">
        <h2 className="text-lg font-bold text-slate-900">Byline queue</h2>
        <p className="text-slate-500 text-sm mt-1">
          Automated stories are assigned a random byline from the rotation pool. Bylines are shared with
          Tempust's manual dropdown; the rotation toggle only affects automation.
        </p>
      </div>

      <div className="flex items-start gap-2 mb-5 p-3 rounded-lg border border-blue-100 bg-blue-50/60 text-xs text-slate-600">
        <Shuffle size={14} className="text-blue-500 mt-0.5 shrink-0" />
        <span>
          {rotationCount > 0
            ? `${rotationCount} byline${rotationCount === 1 ? '' : 's'} in rotation. Each published story picks one at random.`
            : 'No bylines in rotation — stories fall back to the protected byline.'}
        </span>
      </div>

      <div className="flex items-center gap-2 mb-5">
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addByline(); }}
          placeholder="Add a byline (e.g. Newsdesk)"
          className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500"
        />
        <button
          onClick={addByline}
          disabled={adding || !newName.trim()}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
          <Loader2 size={16} className="animate-spin" /> Loading bylines...
        </div>
      ) : active.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">No bylines yet.</div>
      ) : (
        <div className="space-y-2">
          {active.map(b => (
            <BylineRow
              key={b.id}
              byline={b}
              onToggleRotation={toggleRotation}
              onDelete={removeByline}
              onReactivate={reactivateByline}
            />
          ))}
        </div>
      )}

      {inactive.length > 0 && (
        <div className="mt-6">
          <button
            onClick={() => setShowInactive(v => !v)}
            className="text-xs font-medium text-slate-400 hover:text-slate-700 transition-colors"
          >
            {showInactive ? 'Hide' : 'Show'} inactive ({inactive.length})
          </button>
          {showInactive && (
            <div className="space-y-2 mt-3">
              {inactive.map(b => (
                <BylineRow
                  key={b.id}
                  byline={b}
                  onToggleRotation={toggleRotation}
                  onDelete={removeByline}
                  onReactivate={reactivateByline}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}